import { type TurnEnvelope } from "../types/api.js";
import { withCSRF } from "./_csrf.js";

// Must exceed the server's turnParentBudget (internal/turn/handler.go, ~11s)
// so server-side 504s arrive as structured turn_*_timeout errors instead of
// a client-side AbortError.
const TURN_REQUEST_TIMEOUT_MS = 19_000;

type TurnRequestErrorKind = "http" | "client_timeout";

export class TurnRequestError extends Error {
  status?: number;
  body?: string;
  requestId?: string;
  code?: string;
  responseMessage?: string;
  kind: TurnRequestErrorKind;

  constructor(message: string, opts: {
    status?: number;
    body?: string;
    requestId?: string;
    code?: string;
    responseMessage?: string;
    kind?: TurnRequestErrorKind;
  } = {}) {
    super(message);
    this.name = "TurnRequestError";
    this.status = opts.status;
    this.body = opts.body;
    this.requestId = opts.requestId;
    this.code = opts.code;
    this.responseMessage = opts.responseMessage;
    this.kind = opts.kind ?? "http";
  }
}

interface TurnErrorPayload {
  error?: {
    code?: string;
    message?: string;
    request_id?: string;
  };
}

function parseTurnError(body: string): {
  code?: string;
  requestId?: string;
  responseMessage?: string;
} {
  const trimmed = body.trim();
  if (!trimmed) return {};

  try {
    const parsed = JSON.parse(trimmed) as TurnErrorPayload;
    return {
      code: parsed.error?.code,
      requestId: parsed.error?.request_id,
      responseMessage: parsed.error?.message ?? trimmed,
    };
  } catch {
    return { responseMessage: trimmed };
  }
}

const TURN_TIMEOUT_CODES = new Set([
  "client_turn_timeout",
  "turn_stt_timeout",
  "turn_llm_timeout",
  "turn_request_timeout",
  "stt_error", // server returns 502 stt_error from internal/turn/handler.go on Whisper failure — recoverable via retry overlay
]);

export function isTurnTimeoutError(err: unknown): err is TurnRequestError {
  if (!(err instanceof TurnRequestError)) return false;
  if (err.kind === "client_timeout") return true;
  if (err.code && TURN_TIMEOUT_CODES.has(err.code)) return true;
  return err.status === 504;
}

// filenameForMime maps the chosen MediaRecorder MIME to a filename
// extension Whisper sniffs. Server's internal/stt.OpenAISTT does the
// same mapping; FE keeps its own copy since the Blob filename is set
// in the FormData.
function filenameForMime(mime: string): string {
  if (mime.startsWith("audio/webm")) return "speech.webm";
  if (mime.startsWith("audio/mp4"))  return "speech.mp4";
  if (mime.startsWith("audio/ogg"))  return "speech.ogg";
  if (mime.startsWith("audio/wav"))  return "speech.wav";
  return "speech.bin";
}

/** Per-turn timing surfaced to the caller (Phase 1 perf diagnostics). */
export interface TurnTiming {
  /** fetch dispatch → response received, on the client's performance.now() clock. */
  roundtripMs: number;
  serverSttMs?: number;
  serverLlmMs?: number;
  serverTotalMs?: number;
  /** roundtripMs − serverTotalMs (both durations on the client clock; no skew). */
  networkMs?: number;
}

/** parseServerTiming reads a `stt;dur=…, llm;dur=…, total;dur=…` header. */
function parseServerTiming(h: string | null): { stt?: number; llm?: number; total?: number } {
  const out: { stt?: number; llm?: number; total?: number } = {};
  if (!h) return out;
  for (const part of h.split(",")) {
    const m = part.trim().match(/^(\w+);dur=([\d.]+)$/);
    if (!m) continue;
    const v = Math.round(parseFloat(m[2]!));
    if (m[1] === "stt") out.stt = v;
    else if (m[1] === "llm") out.llm = v;
    else if (m[1] === "total") out.total = v;
  }
  return out;
}

export async function postTurn(
  sessionId: string,
  audio: Blob,
  mime: string,
  opts: { signal?: AbortSignal; onTiming?: (t: TurnTiming) => void } = {},
): Promise<TurnEnvelope> {
  const fd = new FormData();
  fd.append("mime", mime);                                 // UI-08 explicit field; server reads MultipartForm.Value["mime"]
  fd.append("audio", audio, filenameForMime(mime));        // explicit filename per Pitfall #5

  const turnStart = performance.now();
  console.info("[turn] POST /turns (audio)", {
    sessionId,
    mime,
    audioBytes: audio.size,
  });

  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TURN_REQUEST_TIMEOUT_MS);

  // Forward an external abort signal (e.g. card-screen pause) to the
  // internal controller so the user-initiated pause cancels the fetch
  // alongside the timeout fallback.
  const externalSignal = opts.signal;
  let onExternalAbort: (() => void) | null = null;
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      onExternalAbort = () => controller.abort();
      externalSignal.addEventListener("abort", onExternalAbort, { once: true });
    }
  }

  try {
    const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/turns`, {
      method: "POST",
      credentials: "include",
      headers: withCSRF(),
      body: fd,
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.text();
      const parsed = parseTurnError(body);
      const requestId = parsed.requestId ?? res.headers.get("X-Request-ID") ?? undefined;
      const parts = [`postTurn: ${res.status}`];
      if (parsed.code) parts.push(parsed.code);
      if (requestId) parts.push(`request_id=${requestId}`);
      if (parsed.responseMessage) parts.push(parsed.responseMessage);
      console.warn("[turn] POST /turns error", {
        sessionId,
        status: res.status,
        code: parsed.code,
        requestId,
        latencyMs: Math.round(performance.now() - turnStart),
        message: parsed.responseMessage,
      });
      throw new TurnRequestError(parts.join(" | "), {
        status: res.status,
        body,
        requestId,
        code: parsed.code,
        responseMessage: parsed.responseMessage,
      });
    }
    const env = (await res.json()) as TurnEnvelope;
    const roundtripMs = Math.round(performance.now() - turnStart);
    const st = parseServerTiming(res.headers.get("Server-Timing"));
    const networkMs = st.total != null ? roundtripMs - st.total : undefined;
    console.info("[turn] POST /turns ok", {
      sessionId,
      requestId: res.headers.get("X-Request-ID") ?? undefined,
      latencyMs: roundtripMs,
      roundtripMs,
      serverSttMs: st.stt,
      serverLlmMs: st.llm,
      serverTotalMs: st.total,
      networkMs,
      evaluation: env.evaluation,
      actionType: env.action_type,
      nextKind: env.next_state?.kind,
      taskShortCode: env.next_state?.kind === "task" ? env.next_state.task_short_code : undefined,
    });
    opts.onTiming?.({
      roundtripMs,
      serverSttMs: st.stt,
      serverLlmMs: st.llm,
      serverTotalMs: st.total,
      networkMs,
    });
    return env;
  } catch (err) {
    if (timedOut && err instanceof DOMException && err.name === "AbortError") {
      throw new TurnRequestError(
        `postTurn: timeout | client_turn_timeout | waited ${TURN_REQUEST_TIMEOUT_MS}ms`,
        {
          code: "client_turn_timeout",
          responseMessage: "request timeout",
          kind: "client_timeout",
        },
      );
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
    if (onExternalAbort && externalSignal) {
      externalSignal.removeEventListener("abort", onExternalAbort);
    }
  }
}

/**
 * Init turn (Phase 9 D-03): POST /api/sessions/:id/turns with an empty
 * application/octet-stream body. Server detects no multipart audio and
 * returns the seed TaskNextState envelope without writing an Attempt row.
 */
export async function postTurnInit(sessionId: string): Promise<TurnEnvelope> {
  const start = performance.now();
  console.info("[turn] POST /turns (init)", { sessionId });
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/turns`, {
    method: "POST",
    credentials: "include",
    headers: withCSRF({}),
    body: new Blob([], { type: "application/octet-stream" }),
  });
  if (!res.ok) {
    console.warn("[turn] POST /turns init error", {
      sessionId,
      status: res.status,
      requestId: res.headers.get("X-Request-ID") ?? undefined,
      latencyMs: Math.round(performance.now() - start),
    });
    throw new TurnRequestError(`postTurnInit: ${res.status}`, { status: res.status });
  }
  const env = (await res.json()) as TurnEnvelope;
  console.info("[turn] POST /turns init ok", {
    sessionId,
    requestId: res.headers.get("X-Request-ID") ?? undefined,
    latencyMs: Math.round(performance.now() - start),
    nextKind: env.next_state?.kind,
    taskShortCode: env.next_state?.kind === "task" ? env.next_state.task_short_code : undefined,
  });
  return env;
}

/**
 * No-input turn (migration 0043): POST /api/sessions/:id/turns with a JSON
 * body `{ "no_input": true }`. Server skips STT + LLM, runs the engine with
 * EvalNoInput (which fires on_correct), writes an attempt row with
 * evaluation='no_input', and returns the next envelope. The card loop
 * uses this after the prompt audio + per-task wait elapses on a
 * no-input-from-kid task.
 *
 * Accepts an AbortSignal so pause-abort cancels the in-flight request
 * cleanly, matching the postTurn pattern.
 */
export async function postTurnNoInput(
  sessionId: string,
  opts?: { signal?: AbortSignal },
): Promise<TurnEnvelope> {
  const start = performance.now();
  console.info("[turn] POST /turns (no_input)", { sessionId });
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/turns`, {
    method: "POST",
    credentials: "include",
    headers: withCSRF({ "Content-Type": "application/json" }),
    body: JSON.stringify({ no_input: true }),
    signal: opts?.signal,
  });
  if (!res.ok) {
    console.warn("[turn] POST /turns no_input error", {
      sessionId,
      status: res.status,
      requestId: res.headers.get("X-Request-ID") ?? undefined,
      latencyMs: Math.round(performance.now() - start),
    });
    throw new TurnRequestError(`postTurnNoInput: ${res.status}`, { status: res.status });
  }
  const env = (await res.json()) as TurnEnvelope;
  console.info("[turn] POST /turns no_input ok", {
    sessionId,
    requestId: res.headers.get("X-Request-ID") ?? undefined,
    latencyMs: Math.round(performance.now() - start),
    nextKind: env.next_state?.kind,
    taskShortCode: env.next_state?.kind === "task" ? env.next_state.task_short_code : undefined,
  });
  return env;
}

/**
 * Client perf marks beaconed to the server (Phase 2). Snake_case mirrors the Go
 * turnMetricsBody / attempt columns. Omitted fields stay NULL server-side.
 */
export interface TurnMetricsBody {
  client_roundtrip_ms?: number;
  client_network_ms?: number;
  client_audio_first_sound_ms?: number;
  client_perceived_ms?: number;
  client_pipeline_ms?: number;
  // Streaming-STT additions: which transport the turn used ("streaming" |
  // "batch") and end-of-speech → envelope-applied latency (the headline
  // streaming win). Logged server-side (turn.metrics.client); not persisted to
  // a column yet (avoids a migration — the feature doesn't depend on it).
  client_transport?: string;
  client_finalize_to_envelope_ms?: number;
}

/**
 * sendTurnMetrics fire-and-forget POSTs the per-turn perf marks. Uses fetch
 * `keepalive` (NOT navigator.sendBeacon) so withCSRF() can set the X-CSRF-Token
 * header that DoubleSubmitCSRF requires; keepalive still lets the request finish
 * if the page is navigating away (e.g. after game_complete). No-op without an
 * attemptId (init turns / insert failures have none). The caller must check the
 * perf-tracking flag before calling — the client must not send when it is off.
 */
export function sendTurnMetrics(sessionId: string, attemptId: string, m: TurnMetricsBody): void {
  if (!attemptId) return;
  try {
    void fetch(
      `/api/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(attemptId)}/metrics`,
      {
        method: "POST",
        credentials: "include",
        headers: withCSRF({ "Content-Type": "application/json" }),
        body: JSON.stringify(m),
        keepalive: true,
      },
    );
  } catch {
    /* fire-and-forget: perf telemetry must never break the turn loop */
  }
}
